import { useEffect, useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { InfoPopover } from '@/components/InfoPopover'
import { Dropdown } from '@/components/cash-collections/components/Dropdown'
import { MultiDropdown } from '@/components/cash-collections/components/MultiDropdown'
import { SortHeader } from '@/components/ui/sort-header'
import { sortRows, type SortState } from '@/lib/useSortable'
import { SegmentedToggle } from './SegmentedToggle'
import { MONO, fmt } from './format'
import type { CreditNotesByHierarchy, DrillTarget, Lens, MeasureTotals, RevenueCategory, RevenueDepartment, RevenueMeasures, RevenueUnit } from '../types'

interface RevenueTableProps {
  /** The schema-v2 tree (Business unit › Department › Category). Undefined on a
   *  v1 bundle, in which case a notice replaces the table. */
  units: RevenueUnit[] | undefined
  monthsInYtd: string[]
  currency: string
  lens: Lens
  lenses: readonly Lens[]
  onLensChange: (lens: Lens) => void
  /** Credit notes per YTD month on the invoice (recognition) axis, aligned to monthsInYtd. */
  creditNotesByMonth: number[]
  /** The same credit notes traced to Business unit › Department (full-window month
   *  arrays). Drives the expandable Credit notes row and the scoped Credit notes / Net
   *  rows; absent on bundles built before the October 2026 breakdown. */
  creditNotesTree?: CreditNotesByHierarchy
  totals: MeasureTotals
  year: number
  onCellClick?: (target: DrillTarget) => void
  /** A department to open on first render (the Overview chips link here with it). */
  initialExpandedDepartment?: string | null
}

/** Browser-session memory of which departments are open (per the plan: the
 *  category level is collapsed by default and remembers its state per session). */
const EXPANDED_KEY = 'sonar.salesReport.expandedDepartments'
const readExpanded = (): string[] => {
  try {
    if (typeof window === 'undefined') return []
    const raw = window.sessionStorage.getItem(EXPANDED_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}
const writeExpanded = (ids: string[]) => {
  try {
    if (typeof window !== 'undefined') window.sessionStorage.setItem(EXPANDED_KEY, JSON.stringify(ids))
  } catch {
    /* storage unavailable — the table still works, it just forgets */
  }
}

/** One rendered line of the tree — a unit, a department or an (expanded) category. */
interface Row {
  kind: 'unit' | 'department' | 'category'
  key: string
  name: string
  node: RevenueMeasures
  unitId: string
  departmentId?: string
  categoryId?: string
  /** Departments: how many categories sit under it (shown as a hint). */
  childCount?: number
  expanded?: boolean
}

type Scope = 'all' | `unit:${string}` | `dept:${string}`

/** Revenue by business unit. Rows: Business unit (bold) › Department (always
 *  shown) › Category (chevron, collapsed by default). The Invoiced/Order lens
 *  toggle re-bases this table only. "Show" scopes the table to one unit or
 *  department; "Categories" re-bases it to a flat list of selected categories. */
export function RevenueTable({ units, monthsInYtd, currency, lens, lenses, onLensChange, creditNotesByMonth, creditNotesTree, totals, year, onCellClick, initialExpandedDepartment }: RevenueTableProps) {
  const recognition = lens === 'Invoiced month'
  const measure = (r: RevenueMeasures) => (recognition ? r.inv : r.ord)
  const measureYtd = (r: RevenueMeasures) => (recognition ? r.invYtd : r.ordYtd)
  const measureYtdPY = (r: RevenueMeasures) => (recognition ? r.invYtdPY : r.ordYtdPY)
  const measurePY = (r: RevenueMeasures) => (recognition ? r.invPY : r.ordPY)

  // --- Expand / collapse (remembered per browser session) ---------------------
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  useEffect(() => {
    const ids = new Set(readExpanded())
    if (initialExpandedDepartment) ids.add(initialExpandedDepartment)
    setExpanded(ids)
  }, [initialExpandedDepartment])
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      writeExpanded([...next])
      return next
    })

  // --- Scope: whole report, one unit, or one department ------------------------
  const [scope, setScope] = useState<Scope>('all')
  const allUnits = units ?? []
  const scopedUnits: RevenueUnit[] = useMemo(() => {
    if (scope === 'all') return allUnits
    if (scope.startsWith('unit:')) return allUnits.filter((u) => u.id === scope.slice(5))
    const deptId = scope.slice(5)
    return allUnits
      .filter((u) => u.departments.some((d) => d.id === deptId))
      .map((u) => ({ ...u, departments: u.departments.filter((d) => d.id === deptId) }))
  }, [allUnits, scope])
  const scopeOptions = [
    { value: 'all', label: 'All business units' },
    ...allUnits.flatMap((u) => [
      { value: `unit:${u.id}`, label: u.name },
      ...u.departments.map((d) => ({ value: `dept:${d.id}`, label: `${u.name} › ${d.name}` })),
    ]),
  ]
  const scoped = scope !== 'all'

  // Column totals come from what is shown: the units, or — scoped to one
  // department — that department.
  const totalNodes: RevenueMeasures[] = scope.startsWith('dept:') ? scopedUnits.flatMap((u) => u.departments) : scopedUnits
  const sum = (f: (r: RevenueMeasures) => number) => totalNodes.reduce((t, r) => t + f(r), 0)
  const monthTotal = (mi: number) => sum((r) => measure(r)[mi] ?? 0)

  // --- Credit notes (Invoiced lens). Full scope: the report's credit notes, expandable
  // to business unit › department. Scoped to a unit or a department: that node's own
  // credit notes (from the traced tree) and its Net. Without the tree (older bundles)
  // the rows only show at full scope, as before. ----------------------------------
  const [cnOpen, setCnOpen] = useState(false)
  const scopeUnitId = scope.startsWith('unit:') ? scope.slice(5) : null
  const scopeDeptId = scope.startsWith('dept:') ? scope.slice(5) : null
  const cnRows = useMemo(() => {
    type CnLine = { key: string; kind: 'unit' | 'department'; id: string; name: string; months: number[]; ytd: number }
    const tree = creditNotesTree
    if (!tree) return scoped ? null : { months: creditNotesByMonth, lines: [] as CnLine[] }
    const idx = monthsInYtd.map((m) => tree.months.indexOf(m))
    const align = (arr: number[] | undefined) => idx.map((i) => (i >= 0 ? (arr?.[i] ?? 0) : 0))
    const line = (key: string, kind: CnLine['kind'], id: string, name: string, arr: number[]): CnLine => {
      const months = align(arr)
      return { key, kind, id, name, months, ytd: months.reduce((a, b) => a + b, 0) }
    }
    const deptLines = (u: CreditNotesByHierarchy['units'][number]) =>
      u.departments
        .map((d) => line(`cnd:${u.id}:${d.id}`, 'department', d.id, d.name, d.reversal))
        .filter((l) => l.months.some((v) => Math.abs(v) > 0.005))
    if (scopeUnitId) {
      const u = tree.units.find((x) => x.id === scopeUnitId)
      return { months: align(u?.reversal), lines: u ? deptLines(u) : [] }
    }
    if (scopeDeptId) {
      const d = tree.units.flatMap((x) => x.departments).find((x) => x.id === scopeDeptId)
      return { months: align(d?.reversal), lines: [] as CnLine[] }
    }
    const lines = tree.units.flatMap((u) => [line(`cnu:${u.id}`, 'unit', u.id, u.name, u.reversal), ...deptLines(u)])
    return { months: creditNotesByMonth, lines }
  }, [creditNotesTree, creditNotesByMonth, monthsInYtd, scoped, scopeUnitId, scopeDeptId])
  const cnAt = (mi: number) => cnRows?.months[mi] || 0
  const cnYtd = (cnRows?.months ?? []).reduce((a, b) => a + b, 0)
  const cnExpandable = !!cnRows && cnRows.lines.length > 0
  const grossYtd = sum(measureYtd)
  const pipeAt = (mi: number) => sum((r) => r.pipe[mi] || 0)
  const pipeYtdTotal = sum((r) => r.pipeYtd)
  const hasPY = allUnits.some((r) => measureYtdPY(r) != null)
  const grossYtdPY = hasPY ? sum((r) => measureYtdPY(r) ?? 0) : null
  const grossPYAt = (mi: number) => sum((r) => measurePY(r)?.[mi] ?? 0)

  // A YoY delta span. Direction/colour key on the RAW ratio (not the rounded %), so a small
  // decline that rounds to 0 isn't shown as a growth-green up-arrow; an exact 0 is neutral grey.
  const deltaSpan = (curV: number, priorV: number | null | undefined, extra: string) => {
    if (!(priorV && priorV > 0)) return <span className={cn(extra, 'text-stone-300 dark:text-stone-600')}>—</span>
    const raw = curV / priorV - 1
    const pct = Math.round(raw * 100)
    const mag = Math.abs(pct) >= 1000 ? '999+' : Math.abs(pct)
    const tone = pct === 0 ? 'text-stone-400 dark:text-stone-500' : raw >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
    return (
      <span className={cn(extra, tone)} title={`${pct}% vs same period last year`}>
        {pct === 0 ? '' : raw >= 0 ? '▲' : '▼'}{mag}%
      </span>
    )
  }

  // --- Category filter: a flat list of selected categories --------------------
  const allCategories = useMemo(
    () =>
      scopedUnits.flatMap((u) =>
        u.departments.flatMap((d) =>
          d.categories.map((c) => ({ id: c.id, name: c.name, label: `${d.name} › ${c.name}`, unit: u.name, department: d.name, node: c as RevenueMeasures })),
        ),
      ),
    [scopedUnits],
  )
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const categoryOptions = allCategories.map((c) => ({ value: c.id, label: c.label, meta: fmt(measureYtd(c.node), currency) }))
  const validSelected = selectedCategories.filter((id) => allCategories.some((c) => c.id === id))
  const categoryMode = validSelected.length > 0
  const selectedRows = allCategories.filter((c) => validSelected.includes(c.id))
  const selMonths = monthsInYtd.map((_m, i) => selectedRows.reduce((t, r) => t + (measure(r.node)[i] ?? 0), 0))
  const selMonthsPY = monthsInYtd.map((_m, i) => selectedRows.reduce((t, r) => t + (measurePY(r.node)?.[i] ?? 0), 0))
  const selYtd = selectedRows.reduce((t, r) => t + measureYtd(r.node), 0)
  const selYtdPY = selectedRows.reduce((t, r) => t + (measureYtdPY(r.node) ?? 0), 0)
  const selShare = grossYtd ? Math.round((selYtd / grossYtd) * 100) : 0

  const showYoY = hasPY
  const curYtdLabel = showYoY ? `YTD '${String(year % 100).padStart(2, '0')}` : 'YTD'
  const priorYtdLabel = `YTD '${String((year - 1) % 100).padStart(2, '0')}`
  const yoyTds = (curV: number, priorV: number | null | undefined) => (
    <>
      <td className={cn('px-3 py-2 text-right text-stone-500 dark:text-stone-400', MONO)}>
        {priorV != null ? fmt(priorV, currency) : '—'}
      </td>
      <td className={cn('px-3 py-2 text-right text-xs', MONO)}>{deltaSpan(curV, priorV, '')}</td>
    </>
  )
  const emptyYoyTds = (
    <>
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
    </>
  )
  // Small YoY delta shown beneath a month's figure (this month vs the same month last year).
  const pctBelow = (curV: number, priorV: number | null | undefined) =>
    deltaSpan(curV, priorV, 'mt-0.5 block text-[10px] leading-none')

  // --- Click-to-sort. Sorting applies within each sibling group (units, the
  // departments of a unit, the categories of a department) so the tree holds.
  // Empty key = the classification's canonical order. ---------------------------
  const [sort, setSort] = useState<SortState>({ key: '', dir: 'asc' })
  const toggle = (key: string) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }))
  const yoyRatio = (cur: number, prior: number | null | undefined) => (prior && prior > 0 ? cur / prior : -Infinity)
  const nodeValue = (r: { name: string; node: RevenueMeasures }, key: string): number | string => {
    if (key === 'name') return r.name
    if (key === 'ytd') return measureYtd(r.node)
    if (key === 'ytdPY') return measureYtdPY(r.node) ?? 0
    if (key === 'yoy') return yoyRatio(measureYtd(r.node), measureYtdPY(r.node))
    if (key[0] === 'm') return measure(r.node)[Number(key.slice(1))] ?? 0
    return 0
  }

  const rows: Row[] = useMemo(() => {
    const out: Row[] = []
    const sortedUnits = sortRows(
      scopedUnits.map((u) => ({ name: u.name, node: u as RevenueMeasures, unit: u })),
      nodeValue,
      sort,
    )
    sortedUnits.forEach(({ unit: u }) => {
      out.push({ kind: 'unit', key: `u:${u.id}`, name: u.name, node: u, unitId: u.id })
      const depts = sortRows(
        u.departments.map((d) => ({ name: d.name, node: d as RevenueMeasures, dept: d })),
        nodeValue,
        sort,
      )
      depts.forEach(({ dept: d }) => {
        const isOpen = expanded.has(d.id)
        out.push({ kind: 'department', key: `d:${d.id}`, name: d.name, node: d, unitId: u.id, departmentId: d.id, childCount: d.categories.length, expanded: isOpen })
        if (!isOpen) return
        const cats = sortRows(
          d.categories.map((c) => ({ name: c.name, node: c as RevenueMeasures, cat: c })),
          nodeValue,
          sort,
        )
        cats.forEach(({ cat: c }) => {
          out.push({ kind: 'category', key: `c:${d.id}:${c.id}`, name: c.name, node: c, unitId: u.id, departmentId: d.id, categoryId: c.id })
        })
      })
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedUnits, expanded, sort, recognition])
  type CatRow = (typeof selectedRows)[number]
  const sortedCategoryRows = sortRows(selectedRows, (r: CatRow, key) => nodeValue(r, key), sort)

  const cellButton = (r: Row, v: number, mi: number) => {
    if (r.kind === 'unit' || !onCellClick) return <span className={cn('block', MONO)}>{fmt(v, currency)}</span>
    const target: DrillTarget = {
      level: r.kind,
      unitId: r.unitId,
      departmentId: r.departmentId!,
      ...(r.categoryId ? { categoryId: r.categoryId } : {}),
      month: monthsInYtd[mi],
    }
    return (
      <button
        type="button"
        onClick={() => onCellClick(target)}
        title={r.kind === 'department' ? 'See this month by category' : 'See the products in this category'}
        className={cn(
          '-mx-1 rounded px-1 hover:bg-champagne-100/70 hover:text-stone-900 dark:hover:bg-champagne-500/25 dark:hover:text-champagne-50',
          MONO,
        )}
      >
        {fmt(v, currency)}
      </button>
    )
  }

  const nameCell = (r: Row) => {
    if (r.kind === 'unit') {
      return (
        <span className="flex items-center gap-1.5 font-semibold text-stone-900 dark:text-stone-50">
          {r.name}
          {r.node.pipeline && (
            <span className="text-champagne-500" title="carries pipeline">
              •
            </span>
          )}
        </span>
      )
    }
    if (r.kind === 'department') {
      return (
        <button
          type="button"
          onClick={() => toggleExpanded(r.departmentId!)}
          aria-expanded={r.expanded}
          aria-label={`${r.expanded ? 'Collapse' : 'Expand'} ${r.name}`}
          className="flex items-center gap-1 whitespace-nowrap pl-3 text-left font-medium text-stone-800 hover:text-stone-900 dark:text-stone-100 dark:hover:text-white"
        >
          <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform', r.expanded && 'rotate-90')} />
          <span>{r.name}</span>
          <span className="hidden text-[11px] font-normal text-stone-400 dark:text-stone-500 lg:inline">
            · {r.childCount} {r.childCount === 1 ? 'category' : 'categories'}
          </span>
          {r.node.pipeline && (
            <span className="text-champagne-500" title="carries pipeline">
              •
            </span>
          )}
        </button>
      )
    }
    return (
      <span className="flex max-w-[260px] items-center gap-1.5 pl-9 text-stone-600 dark:text-stone-300">
        <span className="h-1 w-1 shrink-0 rounded-full bg-stone-300 dark:bg-stone-600" aria-hidden />
        {r.name}
      </span>
    )
  }

  const rowTone = (r: Row) =>
    r.kind === 'unit'
      ? 'bg-stone-50/70 dark:bg-stone-800/40'
      : r.kind === 'category'
        ? 'bg-white text-stone-600 dark:bg-stone-900'
        : 'bg-white dark:bg-stone-900'

  if (!units) {
    return (
      <div className="rounded-2xl border border-stone-200 bg-white p-5 dark:border-stone-800 dark:bg-stone-900">
        <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Revenue by business unit · YTD {year}</h3>
        <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
          This data bundle predates the Business unit › Department › Category view. Upload a refresh built with the current export (schema v2) to see it.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-5">
        <div>
          <h3 className="inline-flex items-center gap-1 text-sm font-semibold text-stone-800 dark:text-stone-100">
            Revenue by business unit · YTD {year}
            <InfoPopover term="businessUnit" />
          </h3>
          <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            {categoryMode
              ? `${recognition ? 'Revenue (Inv)' : 'True Sale (Ord)'} by category · showing ${selectedRows.length} selected ${selectedRows.length === 1 ? 'category' : 'categories'}`
              : (recognition
                  ? 'Inv — Revenue recognised on the invoice-date month (finance view: what was billed, net of PC credit)'
                  : 'Ord — True Sale on the order-created month (sales view: what was sold, net of PC credit), with Pipeline broken out by month below') +
                ' · open a department for its categories; click a department cell for that month by category, a category cell for its products'}
          </p>
        </div>
        <span className="inline-flex flex-wrap items-center gap-2">
          <Dropdown label="Show" value={scope} options={scopeOptions} onChange={(v) => setScope(v as Scope)} align="right" />
          <MultiDropdown
            label="Categories"
            allLabel="All categories"
            options={categoryOptions}
            selected={validSelected}
            onChange={setSelectedCategories}
            onClear={() => setSelectedCategories([])}
            onSelectAll={() => setSelectedCategories(allCategories.map((c) => c.id))}
            align="right"
          />
          <span className="inline-flex items-center gap-1">
            <SegmentedToggle options={lenses} value={lens} onChange={onLensChange} size="sm" ariaLabel="Lens" />
            <InfoPopover term="recognitionVsOrderToggle" />
          </span>
        </span>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-sm" style={showYoY ? { minWidth: 860 } : undefined}>
          <thead>
            <tr className="border-y border-stone-100 dark:border-stone-800">
              <th className="sticky left-0 z-10 min-w-[240px] bg-white px-5 py-2 text-left text-xs font-medium text-stone-500 dark:bg-stone-900 dark:text-stone-400">
                <SortHeader label={categoryMode ? 'Category' : 'Business unit › Department'} sortKey="name" active={sort.key === 'name'} dir={sort.dir} onSort={toggle} align="left" />
              </th>
              {monthsInYtd.map((m, mi) => (
                <th
                  key={m}
                  className={cn(
                    'px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400',
                    recognition && 'bg-emerald-50/40 dark:bg-emerald-500/5',
                  )}
                >
                  <SortHeader label={m} sortKey={`m${mi}`} active={sort.key === `m${mi}`} dir={sort.dir} onSort={toggle} />
                </th>
              ))}
              <th className="px-3 py-2 text-right text-xs font-semibold text-stone-600 dark:text-stone-300">
                <SortHeader label={curYtdLabel} sortKey="ytd" active={sort.key === 'ytd'} dir={sort.dir} onSort={toggle} />
              </th>
              {showYoY && (
                <>
                  <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">
                    <SortHeader label={priorYtdLabel} sortKey="ytdPY" active={sort.key === 'ytdPY'} dir={sort.dir} onSort={toggle} />
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-stone-600 dark:text-stone-300">
                    <SortHeader label="YoY" sortKey="yoy" active={sort.key === 'yoy'} dir={sort.dir} onSort={toggle} />
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {categoryMode ? (
              <>
                {sortedCategoryRows.map((r) => (
                  <tr key={r.id} className="border-b border-stone-50 [&>td]:align-top dark:border-stone-800/60">
                    <td className="sticky left-0 z-10 bg-white px-5 py-2 dark:bg-stone-900">
                      <span className="block truncate font-medium text-stone-800 dark:text-stone-100" title={r.name}>{r.name}</span>
                      <span className="block truncate text-xs text-stone-400 dark:text-stone-500">{r.unit} › {r.department}</span>
                    </td>
                    {monthsInYtd.map((_m, mi) => {
                      const v = measure(r.node)[mi] ?? 0
                      return (
                        <td key={mi} className={cn('px-3 py-2 text-right', recognition && 'bg-emerald-50/30 dark:bg-emerald-500/5', MONO)}>
                          <span className="block">{v ? fmt(v, currency) : '—'}</span>
                          {showYoY && pctBelow(v, measurePY(r.node)?.[mi])}
                        </td>
                      )
                    })}
                    <td className={cn('px-3 py-2 text-right font-semibold', MONO)}>{fmt(measureYtd(r.node), currency)}</td>
                    {showYoY && yoyTds(measureYtd(r.node), measureYtdPY(r.node))}
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-200 [&>td]:align-top dark:border-stone-700">
                  <td className="sticky left-0 z-10 bg-white px-5 py-2 font-semibold text-stone-800 dark:bg-stone-900 dark:text-stone-100">
                    Selected total
                  </td>
                  {selMonths.map((v, mi) => (
                    <td key={mi} className={cn('px-3 py-2 text-right font-semibold', recognition && 'bg-emerald-50/40 dark:bg-emerald-500/5', MONO)}>
                      <span className="block">{v ? fmt(v, currency) : '—'}</span>
                      {showYoY && pctBelow(v, selMonthsPY[mi])}
                    </td>
                  ))}
                  <td className={cn('px-3 py-2 text-right font-bold', MONO)}>{fmt(selYtd, currency)}</td>
                  {showYoY && yoyTds(selYtd, selYtdPY)}
                </tr>
              </>
            ) : (
              <>
                {rows.map((r) => (
                  <tr
                    key={r.key}
                    data-row-kind={r.kind}
                    className={cn(
                      'border-b border-stone-50 [&>td]:align-top dark:border-stone-800/60',
                      r.kind !== 'unit' && 'hover:bg-stone-50/60 dark:hover:bg-stone-800/30',
                      rowTone(r),
                    )}
                  >
                    <td className={cn('sticky left-0 z-10 px-5 py-2', rowTone(r))}>{nameCell(r)}</td>
                    {monthsInYtd.map((_m, mi) => {
                      const v = measure(r.node)[mi] ?? 0
                      return (
                        <td
                          key={mi}
                          className={cn(
                            'px-3 py-2 text-right',
                            recognition && 'bg-emerald-50/30 dark:bg-emerald-500/5',
                            r.kind === 'unit' && 'font-semibold',
                          )}
                        >
                          {cellButton(r, v, mi)}
                          {showYoY && pctBelow(v, measurePY(r.node)?.[mi])}
                        </td>
                      )
                    })}
                    <td className={cn('px-3 py-2 text-right', r.kind === 'unit' ? 'font-bold' : 'font-semibold', MONO)}>{fmt(measureYtd(r.node), currency)}</td>
                    {showYoY && yoyTds(measureYtd(r.node), measureYtdPY(r.node))}
                  </tr>
                ))}
                <tr className="border-t-2 border-stone-200 [&>td]:align-top dark:border-stone-700">
                  <td className="sticky left-0 z-10 bg-white px-5 py-2 font-semibold text-stone-800 dark:bg-stone-900 dark:text-stone-100">
                    {scoped ? 'Total (shown)' : 'Total'}
                  </td>
                  {monthsInYtd.map((_m, mi) => (
                    <td
                      key={mi}
                      className={cn(
                        'px-3 py-2 text-right font-semibold',
                        recognition && 'bg-emerald-50/40 dark:bg-emerald-500/5',
                        MONO,
                      )}
                    >
                      <span className="block">{fmt(monthTotal(mi), currency)}</span>
                      {showYoY && pctBelow(monthTotal(mi), grossPYAt(mi))}
                    </td>
                  ))}
                  <td className={cn('px-3 py-2 text-right font-bold', MONO)}>{fmt(grossYtd, currency)}</td>
                  {showYoY && yoyTds(grossYtd, grossYtdPY)}
                </tr>
                {!recognition && (
                  <tr className="border-t border-stone-100 dark:border-stone-800/60">
                    <td className="sticky left-0 z-10 bg-white px-5 py-2 text-xs font-medium text-champagne-700 dark:bg-stone-900 dark:text-champagne-300">
                      <span className="inline-flex items-center gap-1">Pipeline <InfoPopover term="pipe" /></span>
                    </td>
                    {monthsInYtd.map((_m, mi) => (
                      <td key={mi} className={cn('bg-champagne-50/20 px-3 py-2 text-right text-champagne-700 dark:bg-champagne-500/5 dark:text-champagne-300', MONO)}>
                        {pipeAt(mi) ? fmt(pipeAt(mi), currency) : '—'}
                      </td>
                    ))}
                    <td className={cn('px-3 py-2 text-right font-semibold text-champagne-700 dark:text-champagne-300', MONO)}>{fmt(pipeYtdTotal, currency)}</td>
                    {showYoY && emptyYoyTds}
                  </tr>
                )}
                {recognition && cnRows && (
                  <>
                    <tr data-row-kind="credit-notes" className="border-t border-stone-100 dark:border-stone-800/60">
                      <td className="sticky left-0 z-10 bg-white px-5 py-2 text-xs font-medium text-stone-500 dark:bg-stone-900 dark:text-stone-400">
                        <span className="inline-flex items-center gap-1">
                          {cnExpandable ? (
                            <button
                              type="button"
                              onClick={() => setCnOpen((o) => !o)}
                              aria-expanded={cnOpen}
                              aria-label={`${cnOpen ? 'Collapse' : 'Expand'} credit notes by ${scopeUnitId ? 'department' : 'business unit and department'}`}
                              className="inline-flex items-center gap-1 whitespace-nowrap text-left hover:text-stone-800 dark:hover:text-stone-100"
                            >
                              <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform', cnOpen && 'rotate-90')} />
                              Credit notes
                            </button>
                          ) : (
                            'Credit notes'
                          )}
                          <InfoPopover term="creditNotes" />
                        </span>
                      </td>
                      {monthsInYtd.map((_m, mi) => (
                        <td key={mi} className={cn('bg-emerald-50/20 px-3 py-2 text-right text-red-600 dark:bg-emerald-500/5 dark:text-red-400', MONO)}>
                          {cnAt(mi) ? '−' + fmt(cnAt(mi), currency) : '—'}
                        </td>
                      ))}
                      <td data-ytd={cnYtd} className={cn('px-3 py-2 text-right font-semibold text-red-600 dark:text-red-400', MONO)}>−{fmt(cnYtd, currency)}</td>
                      {showYoY && emptyYoyTds}
                    </tr>
                    {cnOpen &&
                      cnRows.lines.map((l) => (
                        <tr key={l.key} data-row-kind={`cn-${l.kind}`} className="border-t border-stone-50 dark:border-stone-800/60">
                          <td className="sticky left-0 z-10 bg-white px-5 py-1.5 text-xs dark:bg-stone-900">
                            <span
                              className={cn(
                                'flex items-center gap-1.5',
                                l.kind === 'unit' ? 'pl-5 font-medium text-stone-600 dark:text-stone-300' : cn('text-stone-500 dark:text-stone-400', scopeUnitId ? 'pl-5' : 'pl-9'),
                              )}
                            >
                              {l.kind === 'department' && <span className="h-1 w-1 shrink-0 rounded-full bg-stone-300 dark:bg-stone-600" aria-hidden />}
                              {l.name}
                              {l.id === 'unallocated' && <InfoPopover term="creditNotesUnallocated" />}
                            </span>
                          </td>
                          {l.months.map((v, mi) => (
                            <td key={mi} className={cn('bg-emerald-50/10 px-3 py-1.5 text-right text-xs text-red-600 dark:bg-emerald-500/5 dark:text-red-400', MONO)}>
                              {Math.abs(v) > 0.005 ? '−' + fmt(v, currency) : '—'}
                            </td>
                          ))}
                          <td data-ytd={l.ytd} className={cn('px-3 py-1.5 text-right text-xs text-red-600 dark:text-red-400', l.kind === 'unit' && 'font-semibold', MONO)}>−{fmt(l.ytd, currency)}</td>
                          {showYoY && emptyYoyTds}
                        </tr>
                      ))}
                    <tr data-row-kind="net" className="border-t border-stone-100 dark:border-stone-800/60">
                      <td className="sticky left-0 z-10 bg-white px-5 py-2 text-xs font-semibold text-stone-700 dark:bg-stone-900 dark:text-stone-200">
                        <span className="inline-flex items-center gap-1">{scoped ? 'Net (shown)' : 'Net'} <InfoPopover term="netAfterCreditNotes" /></span>
                      </td>
                      {monthsInYtd.map((_m, mi) => (
                        <td key={mi} className={cn('bg-emerald-50/30 px-3 py-2 text-right font-semibold dark:bg-emerald-500/5', MONO)}>
                          {fmt(monthTotal(mi) - cnAt(mi), currency)}
                        </td>
                      ))}
                      <td data-ytd={grossYtd - cnYtd} className={cn('px-3 py-2 text-right font-bold', MONO)}>{fmt(grossYtd - cnYtd, currency)}</td>
                      {showYoY && emptyYoyTds}
                    </tr>
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 border-t border-stone-100 px-5 py-3 text-[11px] text-stone-400 dark:border-stone-800 dark:text-stone-500">
        {categoryMode ? (
          <span>
            Subtotal of {selectedRows.length} selected {selectedRows.length === 1 ? 'category' : 'categories'} ·{' '}
            <span className={MONO}>{selShare}%</span> of {scoped ? 'the shown' : ''} YTD · {recognition ? 'Inv' : 'Ord'} basis. Pipeline, credit notes and net are
            unit-level and don&apos;t apply to a category subtotal.
          </span>
        ) : scoped ? (
          <span>
            Showing one {scopeUnitId ? 'business unit' : 'department'} · {recognition ? 'Inv' : 'Ord'} basis.{' '}
            {!recognition
              ? 'Credit notes and net appear on the Invoiced month lens.'
              : !creditNotesTree
                ? 'Credit notes and net are report-wide, so they appear only with all business units shown.'
                : scopeUnitId
                  ? 'Credit notes are this business unit’s, on the month each was raised, including any filed under it that none of its departments carries (Unallocated).'
                  : 'Credit notes are this department’s own, on the month each was raised; credit notes its business unit could not place in a department (Unallocated) are not included.'}
          </span>
        ) : (
          <>
            <span className="inline-flex items-center gap-1">Full window: Ord <InfoPopover term="ord" /></span>
            <span className={MONO}>{fmt(totals.ord, currency)}</span>
            <span className="inline-flex items-center gap-1">= Inv <InfoPopover term="inv" /></span>
            <span className={MONO}>{fmt(totals.inv, currency)}</span>
            {(totals.priorYearDelivered ?? 0) > 0.5 && (
              <span
                className="inline-flex items-center gap-1"
                title="Revenue in the Inv column delivered from orders placed before the window (2024 and earlier) but recognised in-window — courses/events sold in 2024 and run in 2025 (Kois & Coachman), prior-cohort PC cases and Consultancy. Subtracted here because True Sale (Ord) stays order-based."
              >
                − delivered from prior-year orders{' '}
                <span className={MONO}>{fmt(totals.priorYearDelivered ?? 0, currency)}</span>
              </span>
            )}
            <span className="inline-flex items-center gap-1">+ Pipe <InfoPopover term="pipe" /></span>
            <span className={MONO}>{fmt(totals.pipe, currency)}</span>
            <span>+ after-window <span className={MONO}>{fmt(totals.afterWindow, currency)}</span>.{' '}
              {(totals.priorYearDelivered ?? 0) > 0.5
                ? 'Ord ties order-based; the Inv column also carries prior-year deliveries, subtracted above so the period reconciles.'
                : 'The three measures reconcile at the period total, not cell by cell.'}</span>
          </>
        )}
      </div>
    </div>
  )
}

/** Resolve a DrillTarget against the tree (used by the page to feed the modal). */
export function resolveDrill(units: RevenueUnit[] | undefined, target: DrillTarget | null): { unit: RevenueUnit; department: RevenueDepartment; category?: RevenueCategory } | null {
  if (!units || !target) return null
  const unit = units.find((u) => u.id === target.unitId)
  const department = unit?.departments.find((d) => d.id === target.departmentId)
  if (!unit || !department) return null
  if (target.level === 'category') {
    const category = department.categories.find((c) => c.id === target.categoryId)
    return category ? { unit, department, category } : null
  }
  return { unit, department }
}
